import { createContext, PropsWithChildren, ReactElement, useCallback, useContext } from 'react';

import allRoles from '../../data/roles';

export type Role = {
  normalizedId: string;
  name: string;
};

export type RoleName = keyof typeof allRoles;

export const rolesContext = createContext<{
  id?: string;
  displayName?: string;
  email?: string;
  roles: Role[];
  preferredRole?: Role;
}>({
  roles: [],
});

export const useProfile = () => {
  const { id, displayName, email, roles, preferredRole } = useContext(rolesContext);

  const hasRole = useCallback(
    (role: keyof typeof allRoles) =>
      roles.some((r) => r.normalizedId === allRoles[role].toString()),
    [roles],
  );

  const primaryRole =
    preferredRole?.normalizedId || (roles.length > 0 ? roles[0].normalizedId : undefined);

  return {
    id,
    displayName,
    email,
    primaryRole,
    hasRole,
  };
};

interface ProfileProviderProps {
  profile: {
    id: string;
    displayName: string;
    email: string;
    roles: Role[];
    preferredRole?: Role;
  };
}

export function ProfileProvider({
  children,
  profile,
}: PropsWithChildren<ProfileProviderProps>): ReactElement {
  return (
    <rolesContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        id: profile.id,
        displayName: profile.displayName,
        email: profile.email,
        roles: profile.roles || [],
        preferredRole: profile.preferredRole,
      }}
    >
      {children}
    </rolesContext.Provider>
  );
}
